/**
 * Provenance du visiteur, mémorisée à la première page vue et jointe à chaque
 * formulaire envoyé.
 *
 * Pourquoi la mémoriser plutôt que la lire au moment de l'envoi : quelqu'un
 * qui arrive par Google sur /salle-de-bain, lit deux pages puis remplit le
 * formulaire depuis /simulateur n'a plus, à cet instant, ni les paramètres
 * UTM dans l'URL ni un referent externe. Lue au dernier moment, sa demande
 * paraîtrait « directe ». Toutes les demandes finiraient par le paraître,
 * ce qui est exactement ce qu'on observait dans les journaux nginx.
 *
 * Stockage en sessionStorage, volontairement :
 *   - pas de cookie, donc rien à soumettre au bandeau de consentement ;
 *   - pas de localStorage, qui attribuerait une demande faite en novembre à
 *     la campagne qui avait amené le visiteur en septembre.
 *
 * Aucune donnée personnelle n'y transite : une source, un support, une
 * campagne, un chemin de page. L'identité part dans le formulaire lui-même.
 */

const CLE = "tef_attribution";

const PARAMETRES = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "msclkid",
];

const LONGUEUR_MAX = 120;

const tronquer = (valeur, max = LONGUEUR_MAX) =>
  typeof valeur === "string" ? valeur.trim().slice(0, max) : "";

/** Lit l'URL courante et le referent. Appelé une seule fois par session. */
const releverProvenance = () => {
  const releve = {};
  try {
    const parametres = new URLSearchParams(window.location.search);
    PARAMETRES.forEach((nom) => {
      releve[nom] = tronquer(parametres.get(nom) || "");
    });

    // document.referrer est vide quand l'origine est la même : on ne garde
    // donc jamais un enchaînement interne par erreur.
    const referent = document.referrer || "";
    const interne = referent.startsWith(window.location.origin);
    releve.referent_initial = interne ? "" : tronquer(referent, 500);
    releve.page_entree = tronquer(window.location.pathname, 500);
  } catch (e) {
    // navigation privée, stockage coupé, URL exotique : on n'attribue rien,
    // mais on ne casse surtout pas l'envoi du formulaire.
  }
  return releve;
};

/**
 * Provenance de la session, posée au premier appel puis jamais réécrite.
 *
 * L'écriture est protégée : en navigation privée stricte, sessionStorage
 * lève à l'écriture comme à la lecture. Le formulaire doit partir quand même,
 * sans attribution, plutôt que d'échouer.
 */
export const memoriserProvenance = () => {
  try {
    if (window.sessionStorage.getItem(CLE)) return;
    window.sessionStorage.setItem(CLE, JSON.stringify(releverProvenance()));
  } catch (e) {
    /* sans effet : voir ci-dessus */
  }
};

/**
 * L'objet à joindre au corps du formulaire, sous la clé `attribution`.
 * La page d'envoi est ajoutée à la volée : c'est la seule valeur qui doit
 * refléter l'instant de l'envoi et non celui de l'arrivée.
 */
export const attributionCourante = () => {
  let memorise = {};
  try {
    memorise = JSON.parse(window.sessionStorage.getItem(CLE) || "{}");
  } catch (e) {
    memorise = {};
  }
  if (!memorise || typeof memorise !== "object") memorise = {};

  let pageEnvoi = "";
  try {
    pageEnvoi = tronquer(window.location.pathname, 500);
  } catch (e) {
    /* sans effet */
  }

  return { ...memorise, page_envoi: pageEnvoi };
};
